import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Rss } from "lucide-react";
import { Logo } from "@/components/mdh/Nav";
import { SourceBlocks } from "@/components/mdh/markdown";
import { API, BACKEND } from "@/lib/api";


const TAG_STYLES = {
    New: "border-cyan-500/30 bg-cyan-950/50 text-cyan-300",
    Improvement: "border-teal-500/30 bg-teal-950/50 text-teal-300",
    Fix: "border-amber-500/30 bg-amber-950/50 text-amber-300",
    Security: "border-rose-500/30 bg-rose-950/50 text-rose-300",
};

export default function Changelog() {
    const [entries, setEntries] = useState(null);

    useEffect(() => {
        fetch(`${API}/changelog`)
            .then((r) => (r.ok ? r.json() : { entries: [] }))
            .then((d) => setEntries(d.entries || []))
            .catch(() => setEntries([]));
    }, []);

    return (
        <div data-testid="changelog-page" className="min-h-screen">
            <div className="bg-grid-slate pointer-events-none absolute inset-x-0 top-0 h-[420px] opacity-60" aria-hidden="true" />
            <div className="glow-hero pointer-events-none absolute inset-x-0 top-0 h-[420px]" aria-hidden="true" />

            <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800/80 bg-[#0B0F17]/70 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
                    <div className="flex items-center gap-3">
                        <Logo />
                        <span className="rounded border border-cyan-500/30 bg-cyan-950/50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-cyan-300">
                            Changelog
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/"
                            data-testid="changelog-back-link"
                            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-cyan-300"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
                        </Link>
                        <a
                            href={`${API}/changelog.rss`}
                            data-testid="changelog-rss-link"
                            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-cyan-300"
                        >
                            <Rss className="h-3.5 w-3.5 text-cyan-400" /> RSS
                        </a>
                        <Link
                            to="/docs"
                            data-testid="changelog-docs-link"
                            className="hidden items-center gap-1.5 rounded-md border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-200 transition-colors hover:border-cyan-500/50 hover:text-cyan-300 sm:flex"
                        >
                            Read the Docs <ArrowUpRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
            </header>

            <div className="relative mx-auto max-w-3xl px-5 pb-24 pt-32 lg:px-8">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal-400/80">
                    Changelog
                </p>
                <h1
                    data-testid="changelog-title"
                    className="font-hero mt-3 text-3xl font-bold tracking-tight text-slate-100 sm:text-4xl"
                >
                    Every release, on record.
                </h1>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400">
                    Shipped improvements to the delivery engine, newest first. Managed from the
                    admin console — updated without a redeploy.
                </p>

                <div className="mt-14">
                    {entries === null ? (
                        <p className="font-mono text-xs text-slate-500">Loading releases…</p>
                    ) : entries.length === 0 ? (
                        <p className="font-mono text-xs text-slate-500">No releases yet.</p>
                    ) : (
                        entries.map((e) => (
                            <article
                                key={e.id}
                                data-testid={`changelog-entry-${e.id}`}
                                className="relative border-l border-slate-800 pb-14 pl-8 last:pb-0"
                            >
                                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-cyan-400 bg-[#0B0F17] shadow-[0_0_10px_rgba(6,182,212,0.5)]" />
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="font-mono text-sm font-bold text-cyan-300">
                                        {e.version}
                                    </span>
                                    <span
                                        className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest ${TAG_STYLES[e.tag] || TAG_STYLES.New}`}
                                    >
                                        {e.tag}
                                    </span>
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                                        {e.date}
                                    </span>
                                </div>
                                <h2 className="font-display mt-3 text-xl font-bold tracking-tight text-slate-100">
                                    {e.title}
                                </h2>
                                <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-400">
                                    <SourceBlocks source={e.source} />
                                </div>
                            </article>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
